import * as Cookies from '#util/cookies.ts';
import type {
  CheckLogsStatusMessage,
  DisconnectFromLogStreamMessage,
  RequestAllLogsMessage,
  StartFetchingMessage,
  SubscribeToLogStreamMessage,
} from './worker/log-worker-types.ts';

/**
 * Encapsulates our usage of a shared log worker port for communication to the logs web worker.
 * Mostly this is just for convenience, to store the host id and avoid creating messages manually each time.
 */
export class PageWorker {
  port: MessagePort;
  hostId: string;

  constructor(port: MessagePort, hostId: string) {
    this.port = port;
    this.hostId = hostId;
  }

  /** Ask the worker what state it's in (idle, fetching, errored, etc) */
  checkStatus() {
    this.port.postMessage({
      type: 'checkLogsStatus',
      hostId: this.hostId,
    } satisfies CheckLogsStatusMessage);
  }

  /** Ask the worker to send all the logs it has for a particular host */
  requestAllLogs() {
    this.port.postMessage({
      type: 'requestAllLogs',
      hostId: this.hostId,
    } satisfies RequestAllLogsMessage);
  }

  /** Ask the worker to begin fetching data from dnclient */
  startFetching() {
    this.port.postMessage({
      type: 'startFetching',
      hostId: this.hostId,
      authToken: buildAuthToken(),
    } satisfies StartFetchingMessage);
  }

  /** Ask the worker to send new data it gets from dnclient to this shared worker port */
  subscribe() {
    this.port.postMessage({
      type: 'subscribeToLogStream',
      hostId: this.hostId,
    } satisfies SubscribeToLogStreamMessage);
  }

  /** Let the worker know it no longer needs to send this worker port any log or error data */
  disconnect() {
    this.port.postMessage({
      type: 'disconnectFromLogStream',
      hostId: this.hostId,
    } satisfies DisconnectFromLogStreamMessage);
  }
}

function buildAuthToken() {
  const userToken = Cookies.getItem(Cookies.USER_TOKEN_NAME);
  const authToken = Cookies.getItem(Cookies.AUTH_TOKEN_NAME);
  const oidcToken = Cookies.getItem(Cookies.OIDC_TOKEN_NAME);

  if (userToken) {
    return `Bearer ${userToken} ${authToken}`.trim();
  }
  if (oidcToken) {
    return `Bearer ${oidcToken}`;
  }
  return '';
}
